import React, { useState, useEffect, useCallback } from "react";
import { getWheelCount } from "../../services/api";
import {
  BannerContainer,
  Boards,
  Container,
  Header,
  HeaderIcon,
  Separator,
  StyledHeading,
  TableHeading,
} from "./styles";
import Icon from "assets/images/analytics/analytics.svg";
import Dropdown from "../Dropdown";
import { boards, filterOptions } from "./helper";
import { getHallOfFame } from "../../services/api";
import { Card } from "./Card";
import useGetAnalyticsQuery from "../Referrals/hooks/useGetAnalyticsQuery";
import AnalyticsTable from "../Referrals/children/AnalyticsTable";
import DateRangePicker from "../DateRange";
import TablesModal from "../Referrals/children/TablesModal";
import { changeQueryParameters } from "../../utils/helpers";
import { useSearchParams } from "react-router-dom";
import Input from "../Input";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import { FEATURES } from "../../constants/features";
import FeatureEligibility from "../FeatureEligibility";
import CustomLoader from "../../sharedcomponents/CustomLoader";
import TagSkeletonLoader from "../../pages/Explore/components/TagSkeletonLoader";
import CustomPagination from "../Pagination";
import TextView from "../TextView";
import { toast } from "react-toastify";
import IyugaTooltip from "../IyugaTooltip";

export const AnalyticsComponent = () => {
  const [filter, setFilter] = useState("all");
  const [analytics, setAnalytics] = useState({});
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [open, setOpen] = useState(false);
  const [wheelId, setWheelId] = useState(null);
  const [category, setCategory] = useState(null);
  const [isCalendarVisible, setCalendarVisible] = useState(false);
  const [wheelCountData, setWheelCountData] = useState(null);
  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.ANALYTICS);

  const [searchParams, setSearchParams] = useSearchParams();

  const { analyticsData, totalAnalyticsRecords, analyticsLoading } =
    useGetAnalyticsQuery();

  // Debounce search
  useEffect(() => {
    if (searchText === "") {
      setSearchInUrl("");
      return;
    }

    const debounceTimer = setTimeout(() => {
      setSearchInUrl(searchText);
    }, 500);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setSearchInUrl(searchText);
    }
  };

  const handleForceFilterChange = () => {
    setFilter("date");
    setStartDate(null);
    setEndDate(null);
    setCalendarVisible(true);
  };

  const handleFilterChange = (e) => {
    const selectedFilter = e.target.value;
    setFilter(selectedFilter);

    if (selectedFilter !== "date") {
      setStartDate(null);
      setEndDate(null);
      setCalendarVisible(false);
    } else {
      setCalendarVisible(true);
    }
  };

  const handleCancel = () => {
    setSearchText("");
    setSearchInUrl("");
  };

  const setSearchInUrl = (value) => {
    const queryParams = changeQueryParameters(searchParams, {
      search: value,
    });
    setSearchParams(queryParams);
  };

  const getAnalyticsData = useCallback(async () => {
    try {
      const response = await getHallOfFame(filter, startDate, endDate);
      if (response.data) {
        setAnalytics(response.data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setAnalytics({});
    }
  }, [filter, startDate, endDate]);

  useEffect(() => {
    if (filter === "range" && !startDate && !endDate) {
      return;
    }
    getAnalyticsData();
  }, [filter, startDate, endDate, getAnalyticsData]);

  // Fetch wheel count data for tooltip
  useEffect(() => {
    const getWheelCountData = async () => {
      try {
        const response = await getWheelCount();
        setWheelCountData(response.data.data);
      } catch (error) {
        toast.error("Error fetching wheel count data");
        console.error("Error response:", error.response);
      }
    };

    getWheelCountData();
  }, []);

  const handleOpenModal = (columnName, id) => {
    let category = 0;

    switch (columnName) {
      case "Views":
        category = 2;
        break;
      case "Clones":
        category = 0;
        break;
      case "Favorites":
        category = 1;
        break;
      case "Unlocks":
        category = 3;
        break;
      default:
        category = 0;
    }

    setOpen(true);
    setWheelId(id);
    setCategory(category);
  };

  const handleDateSelection = () => {
    if (startDate && endDate) {
      setCalendarVisible(false);
    }
  };

  const formattedDateRange =
    startDate && endDate
      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      : null;

  const rowsPerPage = 10;
  const limit = parseInt(searchParams.get("limit") || String(rowsPerPage));
  const currentPage = parseInt(searchParams.get("offset") || "0") / limit + 1;

  return (
    <Container>
      {loading || (isCheckingEligibility && <CustomLoader />)}
      <BannerContainer>
        <Header>
          <HeaderIcon src={Icon} />
          <StyledHeading>Analytics</StyledHeading>
        </Header>
        <Dropdown
          options={filterOptions}
          value={filter}
          onChange={handleFilterChange}
          style={{ width: filter === "date" ? 170 : 140 }}
          variant="outlined"
          handleForceChange={handleForceFilterChange}
          renderValue={(selected) =>
            selected === "date" && startDate && endDate
              ? formattedDateRange
              : filterOptions.find((option) => option.value === selected)?.label
          }
        />
        {filter === "date" && isCalendarVisible && (
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onComplete={handleDateSelection}
          />
        )}
      </BannerContainer>

      <Boards>
        {boards.map((board) => {
          const boardId = board.id;
          const analyticsData =
            analytics && analytics[boardId] && analytics[boardId].length > 0
              ? analytics[boardId][0]
              : null;

          const viewCount = analyticsData?.[board.key] || 0;
          return loading ? (
            <TagSkeletonLoader
              noOfBoxes={1}
              style={{
                padding: "16px",
                width: "250px !important",
                height: "100px !important",
                borderRadius: "4px !important",
              }}
            />
          ) : (
            <Card
              key={boardId}
              viewCount={viewCount}
              bgColor={board.background}
              subTitle={board.title}
              icon={board.icon}
              image={
                analyticsData?.wheel_preview?.startsWith("http")
                  ? analyticsData?.wheel_preview
                  : board.image
              }
              text={analyticsData?.wheel_name || ""}
              analyticsData={analyticsData}
            />
          );
        })}
      </Boards>
      {isEligible ? (
        <>
          <Separator />

          <div style={{ display: "flex", flexDirection: "column" }}>
            <TableHeading>
              <div
                style={{
                  padding: "0px 0px 20px 0px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IyugaTooltip
                  tooltip={
                    wheelCountData ? (
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{ marginBottom: "8px", fontWeight: "bold" }}
                        >
                          Total number of Created and Cloned Wheels
                        </div>
                        <div>
                          <span style={{ fontWeight: "bold" }}>Created:</span>{" "}
                          {wheelCountData.created_wheels || 0}
                        </div>
                        <div>
                          <span style={{ fontWeight: "bold" }}>Cloned:</span>{" "}
                          {wheelCountData.cloned_wheels || 0}
                        </div>
                      </div>
                    ) : (
                      "Loading wheel count data..."
                    )
                  }
                  placement="top"
                >
                  <TextView style={{ color: "#123051" }} variant="subtitle">
                    Showing{" "}
                    <span style={{ fontWeight: 700 }}>
                      {Math.min(
                        (currentPage - 1) * rowsPerPage + 1,
                        totalAnalyticsRecords
                      )}{" "}
                      -{" "}
                      {Math.min(
                        currentPage * rowsPerPage,
                        totalAnalyticsRecords
                      )}{" "}
                    </span>
                    <span> out of </span>
                    <span style={{ fontWeight: 700 }}>
                      {totalAnalyticsRecords}
                    </span>
                    <span> results</span>
                  </TextView>
                </IyugaTooltip>
              </div>

              <CustomPagination
                currentPage={currentPage}
                totalRecords={totalAnalyticsRecords}
                rowsPerPage={limit}
              />
              <Input
                isSearch={true}
                value={searchText}
                onClear={handleCancel}
                showClearIcon={searchText.length !== 0}
                onKeyUp={handleKeyPress}
                onChange={handleSearch}
                variant={"secondary"}
                containerStyle={{ width: 200 }}
              />
            </TableHeading>

            <AnalyticsTable
              data={analyticsData}
              loading={analyticsLoading}
              totalRecords={totalAnalyticsRecords}
              onItemClicked={handleOpenModal}
              limit={limit}
              currentPage={currentPage}
            />
          </div>

          {open && (
            <TablesModal
              onClose={() => setOpen(false)}
              open={open}
              wheelId={wheelId}
              category={category}
            />
          )}
        </>
      ) : (
        <FeatureEligibility
          title="See How Your Wheels Are Rolling!"
          text={`Upgrade to ${subscription} and get detailed analytics into how users interact with your wheels—track views, unlocks, and cloning to optimize your creations and boost their impact.`}
          titleVariant="title"
          requiredSubscription={requiredSubscription}
        />
      )}
    </Container>
  );
};
